/* eslint-disable */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Box, IconButton, Tooltip, Button, Popper, Paper } from '@mui/material';
import dayjs from 'dayjs';
import { MIS_HEALTH_CHECK, HEALTH_CHECK_OVERVIEW } from '../../../Constants/strings';
import './HealthCheck.scss';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../../Hooks/useAuth';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MIS_PMO_HEALTH_CHECK } from '../../../Api/resources/endPoint';
import AccordionWrapper from '../../../Components/AccordionWrapper/AccordionWrapper';
import { HealthCheckOverviewProvider } from '../../../Contexts/HealthCheckOverviewContext';
import { useHealthCheckOverview } from '../../../Hooks/useHealthCheckOverview';
import downloadIcon from '../../../Assets/images/svg/dar_collection_dashboard_download.svg';
import { ReactComponent as FilterIcon } from '../../../Assets/images/svg/dar_filter_purple_color.svg';
import DataGridComponent from '../../../Components/DataGrid/dataGrid';
import GraphView from './GraphView/graphView';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import exportToExcel from '../../../Utils/exportToExcel';
import CheckboxPopper from '../../../Components/CheckboxPopper/checkboxPopper';
import UseAxios from '../../../Api/http/http';
import { fetchPMOHealthCheck } from '../../../Api/request/pmoHealthCheck';

// Import the PMO Edit Modal
import EditPMOModal from './EditPMOModal';

const fetchBaseURL = () => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);

  if (process.env.NODE_ENV === 'production') {
    return (
      process.env.REACT_APP_API_URL + serviceURL + '/api' + MIS_PMO_HEALTH_CHECK
    );
  } else {
    return (
      process.env.REACT_APP_API_URL +
      `:${servicePort}` +
      serviceURL +
      '/api' +
      MIS_PMO_HEALTH_CHECK
    );
  }
};

const baseURL = fetchBaseURL();

const getProjectId = (rowData) => (
  rowData?.project_seq_id
  ?? rowData?.project?.project_seq_id
  ?? rowData?.project?.projectSeqId
  ?? rowData?.project_id
  ?? rowData?.project?.project_id
  ?? rowData?.project?.projectId
  ?? rowData?.projectId
  ?? null
);

const addProjectNameLinks = (rows = []) => rows.map((row) => {
  if (!getProjectId(row)) return row;

  const rowConfiguration = row.rowConfiguration || {};
  const onClick = rowConfiguration.onClick || [];
  if (onClick.some((item) => item.field === 'project.projectName')) return row;

  return {
    ...row,
    rowConfiguration: {
      ...rowConfiguration,
      onClick: [...onClick, { field: 'project.projectName' }],
    },
  };
});

const HealthCheckAccordion = ({projectDetails, isLoaded}) => {
  const { accordionState, expandIndividual } = useHealthCheckOverview();
  const [selectedTab, setSelectedTab] = useState('Table');
  const { snackbarOpen } = useSnackbar();
  const [model, setModel] = useState([]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const setSortModel = (model) => {
    setModel(model);
  };

  const getDynamicColumnWidths = (columns, rows) => {
    return columns.map((col) => {
      const field = col.field;
      const maxLength = rows.reduce((max, row) => {
        const val = row[field];
        return Math.max(max, val ? String(val).length : 0);
      }, field.length);
      
      let width = maxLength * 8 + 10;
       
      if (field === 'status') {
        width = Math.ceil(4.22 * maxLength + 103);
      }
      return { ...col, width };
    });
  };

  const updatedColumns = getDynamicColumnWidths(
    projectDetails.overviewColumns,
    projectDetails.overviewData
  );

  const handleFileDownload = () => {
    try {
      if (!projectDetails?.overviewData) {
        throw new Error('No data available for download');
      }

      const sortedOverviewData = projectDetails.overviewData;
      
      if (model && model.length > 0) {
          sortedOverviewData.sort((a, b) => {
            const { field, sort } = model[0];
            let aValue = a[field] || '';
            let bValue = b[field] || '';
            
            if(field === 'status') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
            } else {
              aValue = parseFloat(aValue) || 0;
              bValue = parseFloat(bValue) || 0;
            }

            let compareResult = 0;
            if (aValue < bValue) {
              compareResult = sort === 'asc' ? -1 : 1;
            } else if (aValue > bValue) {
              compareResult = sort === 'asc' ? 1 : -1;
            } else {
              compareResult = 0;
            }
            return compareResult;
          });
      }

      const formattedTimestamp = dayjs().format('DDMMMYY');
      const workBookTitle = `HealthCheckPMO-Overview-${formattedTimestamp}`;

      const formattedData = sortedOverviewData.map((statusDetail) => {
        const updatedStatusDetail = { status: statusDetail.status };
        
        for (const key in statusDetail) {
          if (key === 'id' || key === 'status' || key === 'statusColour' || key === 'displayOrder') {
            continue;
          }
          const percentKey = `${key} %`;
          const countKey = `${key} count`;
          const [ percentValue, countValue ] = statusDetail[key].split(' ');
          updatedStatusDetail[percentKey] = percentValue;
          updatedStatusDetail[countKey] = countValue;
        }

        return updatedStatusDetail;
      });

      return exportToExcel({
        data: formattedData,
        workBookTitle: workBookTitle,
        workSheetTitle: workBookTitle,
        filterKeyList: ['id', 'statusColour', 'displayOrder']
      });

    } catch (error) {
      snackbarOpen(error.message, 'error');
    }
  };

  const renderTabContent = () => {    
    switch (selectedTab) {
      case 'Table': 
        return (
          <div className="health-check-data-grid"> 
            {projectDetails.statusLength > 0 ? (
                <DataGridComponent
                  dataRows={projectDetails.overviewData}
                  dataColumns={updatedColumns}
                  pageSize={projectDetails.statusLength}
                  isFilteredRows={[]}
                  sortFilterColumns={updatedColumns.map((column) => column.field)}
                  isEdit={false}
                  gridCheckbox={false}
                  hideFooter={true}
                  getRowId={(row) => row.id}
                  hideAutoheight={projectDetails?.overviewData?.length > 6}
                  updateSortModel={setSortModel}
                />
            ) : (
              <div>Loading...</div>
            )}
          </div>
        );
      case 'Graph':
        return isLoaded ? <GraphView projectDetails={projectDetails} /> : <div>Loading...</div>;
      default:
        return null;
    }
  };

  const tabComponent = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: '0 16px',
        borderBottom: '1px solid #e0e0e0',
        gap: 2,
      }}
      className="healthCheck-overview-tab"
    >
      <Tooltip
        disableInteractive 
        title={'Download Excel'} 
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#fff',
              color: '#49454E',
              fontSize: '12px',
            },
          },
        }}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -8],
                },
              },
            ],
          },
        }}
      >
        <IconButton
          id="export-to-excel-btn"
          className="collection-dashboard-utility-btn health-check-download-btn"
          onClick={handleFileDownload}
          disabled={!isLoaded}
          sx={{opacity: isLoaded ? '1' : '0.5'}}
        >
          <img src={downloadIcon} alt={'Download button'} />
        </IconButton>
      </Tooltip>
      {/* Tabs */}
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Health Check Tabs"
        sx={{
          '& .MuiTabs-indicator': {
            display: 'none',
          },
          '& .Mui-selected': {
            'background-color': '#6B5CE8 !important',
          },
        }}
      >
        <Tab
          value='Table'
          sx={{ textTransform: 'none', 
                fontSize: 16,
                '&.Mui-selected': {
                borderBottom: 'none',
            },
           }}
          className='inner-health-check-tab1'
        />
        <Tab
          value='Graph'
          sx={{ textTransform: 'none', 
                fontSize: 16,
                '&.Mui-selected': {
                borderBottom: 'none',
            },
           }}
          className='inner-health-check-tab2'
        />
      </Tabs>
    </Box>
  );

  const configuration = {
    [HEALTH_CHECK_OVERVIEW.TITLE]: {
      title: HEALTH_CHECK_OVERVIEW.TITLE,
      state: accordionState?.[HEALTH_CHECK_OVERVIEW.TITLE],
      data: (
        <div>
          <hr className='health-check-hr-tag'/>
          {tabComponent}
          <Box 
             className="title-health-check"
             sx={{ padding: 2, marginTop: selectedTab === 'Graph' ? '-3rem' : '0',}}
          >
            {renderTabContent()}
          </Box>
        </div>
      ),
      openHandler: () => expandIndividual(HEALTH_CHECK_OVERVIEW.TITLE),
    },
  };

  return (
    <AccordionWrapper
      backgroundColor={'#F1F5F8'}
      configuration={configuration}
      titleClassname={'healthCheck-overview-table-titles'}
      accordionClassname={'healthCheck-overview-default'}
    />
  );
};

HealthCheckAccordion.propTypes = {
  projectDetails: PropTypes.shape({
    overviewColumns: PropTypes.array.isRequired,
    overviewData: PropTypes.array.isRequired,
    statusLength: PropTypes.number.isRequired,
  }).isRequired,
  isLoaded: PropTypes.bool.isRequired,
};

const HealthCheck = () => {
  const { userProfile } = useAuth();
  const getTableStateRef = useRef(null);
  const [hoveredCellComment, setHoveredCellComment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectDetails, setProjectDetails] = useState({
    overviewData: [],
    overviewColumns: [],
    statusLength: 0,
    pmoStatusList: [],
  });

  const [pmoStatusList, setPMOStatusList] = useState([]);
  const pmoStatusFilterBtnRef = useRef(null);
  const [showPMOStatusFilter, setShowPMOStatusFilter] = useState(false);
  const [selectedPMOStatuses, setSelectedPMOStatuses] = useState([]);
  const [appliedPMOStatuses, setAppliedPMOStatuses] = useState([]);

  // Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editProjectData, setEditProjectData] = useState(null);
  const { snackbarOpen } = useSnackbar();

  // ==========================================
  // PERMISSIONS CHECK
  // ==========================================
  const isSuperAdmin = useMemo(() => {
    return userProfile?.roleDetails?.some((role) =>
      role.roleName?.toLowerCase().includes('super admin')
    );
  }, [userProfile]);

  const misPermissions = userProfile?.permissions?.['mis:health-check:pmo'] || [];
  const canEdit = isSuperAdmin || misPermissions.includes('update') || misPermissions.includes('edit');

  useEffect(() => {
    if (!showPMOStatusFilter) {
      setSelectedPMOStatuses(appliedPMOStatuses);
    }
  }, [showPMOStatusFilter]);

  const closePMOStatusFilter = () => {
    setShowPMOStatusFilter(false);
  };

  const updateSelectedPMOStatuses = (updatedStatuses) => {
    setSelectedPMOStatuses(updatedStatuses);
  };

  const onPMOStatusApplyBtnClick = () => {
    const appliedPMOStatuses = [...selectedPMOStatuses];
    setAppliedPMOStatuses(appliedPMOStatuses);
    applyPMOStatusFilter(appliedPMOStatuses);
  };

  const onPMOStatusResetBtnClick = async () => {
    setSelectedPMOStatuses([]);
    if (appliedPMOStatuses.length) {
      setAppliedPMOStatuses([]);
      await applyPMOStatusFilter([]);
    }
  };

  const applyPMOStatusFilter = async (appliedPMOStatuses) => {
    if (getTableStateRef.current) {
      const { 
        filters, 
        pagination, 
        searchQuery, 
        views,
        setLoading, 
        setTableData,
        setTableConfiguration,
        setPagination
      } = getTableStateRef.current();
      
      const payload = {
        filter: filters,
        pagination: { page: pagination.page, limit: pagination.limit },
        searchQuery: searchQuery,
        selectedViewId: views?.selectedViewId || null,
        userInfo: {
          emailId: userProfile.email,
          permissions: userProfile.permissions,
          roleDetails: userProfile.roleDetails,
          employeeId: userProfile.employeeId,
        },
        additionalFilters: {
          pmoStatus: appliedPMOStatuses,
        },
      };

      try {
        closePMOStatusFilter();
        setLoading(true);
        const response = await UseAxios(fetchPMOHealthCheck(payload));

        let tableConfig = response?.data?.data?.tableConfiguration || [];
        
        // Dynamically hide/show action column based on edit permissions
        tableConfig = tableConfig.map((col) => {
          if (col.field === 'action' || col.type === 'action') {
            const allowedActions = [];
            if (canEdit) allowedActions.push('edit');
            return { ...col, actions: allowedActions, visible: allowedActions.length > 0 };
          }
          return col;
        });

        setTableData(addProjectNameLinks(response?.data?.data?.tableData || []));
        setTableConfiguration(tableConfig);
        setPagination(pagination => ({...pagination, total: response?.data?.meta?.total || pagination.total}));
        
        setProjectDetails({
          overviewData: response?.data?.data?.overviewData || [],
          overviewColumns: response?.data?.data?.overviewColumns || [],
          statusLength: response?.data?.data?.statusLength || 0,
          pmoStatusList: response?.data?.data?.pmoStatusList || [],
        });
      } catch (error) {
        console.log('Error fetching PMO Health Check data on status filter:', error);
      }
      setLoading(false);
    }
  };

  const measureHeaderTextLengths = () => {
    setTimeout(() => {
      const thElements = document.querySelectorAll('th.p-resizable-column');
      if (thElements.length > 0) {
        thElements.forEach((th, index) => {
          const headerSpan = th.querySelector('span.typography.header');
          if (headerSpan) {
            const headerText = headerSpan.textContent || '';
            if (headerText.length > 32 && headerText.length <= 50) {
              headerSpan.classList.add('wrap-long-text');
              const headerColumnDiv = th.querySelector('.header-column');
              if (headerColumnDiv) {
                const svg = headerColumnDiv.querySelector('svg');
                if (svg) {
                  svg.classList.add('margin-adjusted');
                }
                const threeDotButton =
                  headerColumnDiv.querySelector('.three-dot');
                if (threeDotButton) {
                  threeDotButton.classList.add('margin-adjusted');
                }
              }
            }else if(headerText.length > 50 && headerText.length <= 60) {
              headerSpan.classList.add('wrap-midlong-text');
              headerSpan.classList.remove('wrap-longest-text');
              const headerColumnDiv = th.querySelector('.header-column');
              if (headerColumnDiv) {
                const svg = headerColumnDiv.querySelector('svg');
                if (svg) {
                  svg.classList.add('margin-adjusted');
                }
                const threeDotButton =
                  headerColumnDiv.querySelector('.three-dot');
                if (threeDotButton) {
                  threeDotButton.classList.add('margin-adjusted');
                }
              }
            } else if(headerText.length > 60) {
              headerSpan.classList.add('wrap-longest-text');
              const headerColumnDiv = th.querySelector('.header-column');
              if (headerColumnDiv) {
                const svg = headerColumnDiv.querySelector('svg');
                if (svg) {
                  svg.classList.add('margin-adjusted');
                }
                const threeDotButton =
                  headerColumnDiv.querySelector('.three-dot');
                if (threeDotButton) {
                  threeDotButton.classList.add('margin-adjusted');
                }
              }
            } else {
              headerSpan.classList.remove('wrap-long-text');
              const headerColumnDiv = th.querySelector('.header-column');
              if (headerColumnDiv) {
                const svg = headerColumnDiv.querySelector('svg');
                if (svg) {
                  svg.classList.remove('margin-adjusted');
                }
                const threeDotButton =
                  headerColumnDiv.querySelector('.three-dot');
                if (threeDotButton) {
                  threeDotButton.classList.remove('margin-adjusted');
                }
              }
            }
          }
        });
      }
     }, 100);
  };

  useEffect(() => {
    measureHeaderTextLengths();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node.tagName === 'TH' || node.querySelectorAll) {
              measureHeaderTextLengths();
              break;
            }
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const handleApiResponse = useCallback((response) => {
    setLoading(false);
    setProjectDetails({
      overviewData: response.data.overviewData || [],
      overviewColumns: response.data.overviewColumns || [],
      statusLength: response.data.statusLength || 0,
      pmoStatusList: response.data.pmoStatusList || [],
    });

    if (response?.isReset) {
      setSelectedPMOStatuses([]);
      setAppliedPMOStatuses([]);
    }

    if (response?.data?.tableData) {
      response.data.tableData = addProjectNameLinks(response.data.tableData);
    }

    // Process permissions to hide action columns globally from API Initial loads
    if (response?.data?.tableConfiguration) {
      response.data.tableConfiguration = response.data.tableConfiguration.map((col) => {
        if (col.field === 'action' || col.type === 'action') {
          const allowedActions = [];
          if (canEdit) allowedActions.push('edit');
          return { ...col, actions: allowedActions, visible: allowedActions.length > 0 };
        }
        return col;
      });
    }

    return response;
  }, [canEdit]);

  useEffect(() => {
    setPMOStatusList(projectDetails.pmoStatusList);
  }, [projectDetails]);

  const handleCellClick = useCallback((params) => {
    const { action, fieldPath, rowData } = params || {};
    if (fieldPath === 'project.projectName') {
      const projectId = getProjectId(rowData);
      if (!projectId) return;

      const query = new URLSearchParams({ projectId });
      window.open(`/delivery/project-details?${query.toString()}`, '_blank', 'noopener,noreferrer');
      return;
    }

    if (action !== 'edit' || !rowData) return;
    if (!canEdit) {
      snackbarOpen('You do not have permission to edit.', 'error');
      return;
    }
    setEditProjectData(rowData);
    setShowEditModal(true);
  }, [canEdit, snackbarOpen]);

  const handlePMOCellMouseOver = useCallback((event) => {
    const cell = event.target.closest('td');
    const row = cell?.closest('tbody tr');
    const table = cell?.closest('table');
    if (!cell || !row || !table || !getTableStateRef.current) {
      setHoveredCellComment(null);
      return;
    }

    const tableState = getTableStateRef.current();
    const headerCells = table.querySelectorAll('thead tr:last-child th');
    const headerLabel = headerCells[cell.cellIndex]?.textContent?.replace(/\s+/g, ' ').trim();
    const column = tableState?.tableConfiguration?.find((item) => (
      item.field === headerLabel || item.header === headerLabel
    ));
    if (!column?.field) {
      setHoveredCellComment(null);
      return;
    }

    const rowIndex = Array.from(row.parentElement.children).indexOf(row);
    const rowData = tableState?.tableData?.[rowIndex];
    const comment = rowData?.[`${column.field} Comments`];
    if (typeof comment !== 'string' || !comment.trim()) {
      setHoveredCellComment(null);
      return;
    }

    setHoveredCellComment((current) => (
      current?.anchorEl === cell && current?.comment === comment
        ? current
        : { anchorEl: cell, comment }
    ));
  }, []);

  const handleModalSaveSuccess = async () => {
    // Re-fetch the current data using existing state filters
    await applyPMOStatusFilter(appliedPMOStatuses);
  };

  const configuration = {
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      userInfo: {
        emailId: userProfile.email,
        permissions: userProfile.permissions,
        roleDetails: userProfile.roleDetails,
        employeeId: userProfile.employeeId,
      },
      additionalPayloadData: {
        additionalFilters: {
          pmoStatus: appliedPMOStatuses,
        },
      },
      apiEndpoints: {
        getAllViews: { endpoint: 'views', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: 'view', enabled: false },
        deleteView: { endpoint: 'view-delete', enabled: false },
        download: { 
          endpoint: 'export', 
          enabled: true, 
          filename: () => getExportFileName('PMO Health Check'),
          sheetName: () => getExportFileNameWithDate('PMO Health Check') 
        },
      },
      handler: {
        handleFilteredApiResponse: handleApiResponse,
        setTableStateHandler: (handler) => {
          getTableStateRef.current = handler;
        },
        onCellClickHandler: handleCellClick,
      },
      headers: {
        token: userProfile.access_token, // Any additional custom headers
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        // show or hide tool bar elements
        showToolBarHeading: true,
        toolBarHeading: 'Health check details',
        showAddNew: false,
        showDownloadExcel: true,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showExpandCollapse: false,
        showReset: true,
        additionalElements: [
          <Tooltip
            key="pmo-status-filter-tooltip"
            arrow
            disableInteractive
            title={'Filter by PMO Status'}
            componentsProps={{
              tooltip: {
                sx: {
                  pointerEvents: 'none',
                  backgroundColor: '#fff',
                  color: '#49454E',
                  fontSize: '12px',
                  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
                  '& .MuiTooltip-arrow': {
                    fontSize: 6,
                    color: '#000'
                  },
                },
              },
            }}
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, -10],
                    },
                  },
                ],
              },
            }}
          >
            <Button
              disableRipple
              className="status-filter-btn"
              ref={pmoStatusFilterBtnRef}
              onClick={() => {
                setShowPMOStatusFilter((showPMOStatusFilter) => !showPMOStatusFilter);
              }}
            >
              <FilterIcon />
            </Button>
          </Tooltip>,
        ],
      },
    },
    tableSettings: {
      size: 'small',
      loaderSkeleton: {
        rows: 10,
        bodyCells: 2,
        columns: 5,
      },
      showExpandCollapse: true,
      tableScrollHeight: '52vh',
      maxFrozenColumns: {
        desktop: 4,
        tablet: 2,
        mobile: 1,
      },
    },
  };

  // Safely extract project ID from Darpan's highly nested row data
  const targetProjectId = editProjectData?.project?.projectId 
    || editProjectData?.project_id 
    || editProjectData?.projectId 
    || editProjectData?.id;

  const targetProjectName = editProjectData?.project?.projectName 
    || editProjectData?.project_name 
    || editProjectData?.['Project Name'] 
    || 'Project';

  return (
    <div className="health-check-outer-layer">
      <div className="health-check">
        <h1 className="health-check-title">{MIS_HEALTH_CHECK.TITLE}</h1>
        
        <div className='health-check-accordion'>
          <HealthCheckOverviewProvider>
            <HealthCheckAccordion projectDetails={projectDetails} isLoaded={!loading} />
          </HealthCheckOverviewProvider>

          <CheckboxPopper 
            anchorEl={pmoStatusFilterBtnRef.current}
            open={showPMOStatusFilter}
            onClose={closePMOStatusFilter}
            options={pmoStatusList}
            selectedItems={selectedPMOStatuses}
            onChange={updateSelectedPMOStatuses}
            enableSearch={true}
            searchInputPlaceholder="Search status..."
            notFoundText="No status found"
            onReset={onPMOStatusResetBtnClick}
            onApply={onPMOStatusApplyBtnClick}
          />
        </div>
        
        {/* ADDED THE EXACT & WRAPPER CLASS HERE */}
        <div
          className={`table-utility ${canEdit ? 'has-edit-columns' : ''}`}
          onMouseOver={handlePMOCellMouseOver}
          onMouseLeave={() => setHoveredCellComment(null)}
        >
          <TableUtility configuration={configuration} />
        </div>

        <Popper
          open={Boolean(hoveredCellComment)}
          anchorEl={hoveredCellComment?.anchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          sx={{ zIndex: 1500, pointerEvents: 'none' }}
        >
          <Paper
            elevation={2}
            sx={{
              minWidth: 304,
              maxWidth: 420,
              p: 1.5,
              border: '1px solid #d2d2d2',
              borderRadius: '8px',
              color: '#333',
              backgroundColor: '#fff',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {hoveredCellComment?.comment}
          </Paper>
        </Popper>

      </div>

      {/* Render PMO Edit Modal */}
      {showEditModal && targetProjectId && (
        <EditPMOModal
          open={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setEditProjectData(null);
          }}
          projectId={targetProjectId}
          projectName={targetProjectName}
          baseURL={baseURL}
          token={userProfile?.access_token || localStorage.getItem('authToken') || ''}
          onSaveSuccess={handleModalSaveSuccess}
        />
      )}
    </div>
  );
};

export default HealthCheck;
